import React from "react";

// ✅ Fixed compliance items list
// ✅ Fixed compliance config with default regulation
const complianceConfig = [
  { item: "Organic", regulation: "Regulation (EC) No 834/2007" },
  { item: "Food Safety", regulation: "Regulation (EC) No 178/2002" },
  { item: "Hygiene", regulation: "Regulation (EC) No 852/2004" },
  { item: "Allergen", regulation: "Regulation (EC) No 1169/2011" },
  { item: "Microbiology", regulation: "Regulation (EC) No 2073/2005" },
  { item: "Contaminants", regulation: "Regulation (EC) No 1881/2006" },
  { item: "Pesticides", regulation: "Regulation (EC) No 396/2005" },
  { item: "Solvent", regulation: "Directive 2009/32/EC" },
  { item: "GMO", regulation: "Regulation (EC) No 1829/2003, 1830/2003" },
  { item: "Packing", regulation: "Regulation (EC) No 1935/2004, 10/2011" },
  { item: "Ionisation / Radiation", regulation: "Directive 1999/2/EC" },
  { item: "TSE/BSE", regulation: "Regulation (EC) No 999/2001" },
  { item: "Food Additives", regulation: "Regulation (EC) No 231/2012, 1333/2008" },
  { item: "PCB", regulation: "Regulation (EC) No 1259/2011" },
  { item: "PAH", regulation: "Regulation (EC) No 2005/108" },
];


const ComplianceTable = ({ data = [], onChange }) => {

  const handleChange = (item, key, value) => {
    const updated = [...data];
    const idx = updated.findIndex((row) => row.complianceItem === item);

        const cfg = complianceConfig.find(c => c.item === item);

    if (idx >= 0) {
      updated[idx] = { ...updated[idx], complianceItem: item,
        regulation: cfg.regulation, [key]: value };
    } else {
      updated.push({
        complianceItem: item,
        regulation: cfg.regulation,   // always fixed
        [key]: value,
      });
    }

    onChange("productCompliance", updated);
  };
  const getValue = (item, key) => {
    const row = data.find((r) => r.complianceItem === item);
        return row ? row[key] || "" : "";
  };

  return (
    <div className="mb-3">
      <h5 className="mt-3 text-success">Product Compliance</h5>

      <table className="table table-bordered">
        <thead className="table-light">
          <tr>
            <th>Compliance Item</th>
            <th>Regulation</th>
            <th>Status</th>
            <th>Declaration</th>
          </tr>
        </thead>

        <tbody>
          {complianceConfig.map((cfg, idx) => (
            <tr key={idx}>
              <td>{cfg.item}</td>

              {/* Regulation (prefilled but editable) */}
              <td>
                <input
                  type="text"
                  className="form-control"
                  value={cfg.regulation}
                  readOnly
                 disabled
                />
              </td>

              {/* Status */}
              <td>
                <select
                  className="form-control"
                  value={getValue(cfg.item, "status")}
                  onChange={(e) =>
                    handleChange(cfg.item, "status", e.target.value)
                  }
                >
                  <option value="">-- Select --</option>
                  <option value="COMPLIANT">Compliant</option>
                  <option value="NON_COMPLIANT">Non-Compliant</option>
                  <option value="NOT_APPLICABLE">Not Applicable</option>
                </select>
              </td>

              {/* Declaration */}
              <td>
                <input
                  type="text"
                  className="form-control"
                  value={getValue(cfg.item, "declaration")}
                  onChange={(e) =>
                    handleChange(cfg.item, "declaration", e.target.value)
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ✅ Reusable dynamic list section
const DynamicListSection = ({ title, field, data, onChange, schema, emptyObject }) => {
  const items = data[field] || [];

  const handleListChange = (index, key, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [key]: value };
    onChange(field, updated);
  };

  const addListItem = () => onChange(field, [...items, emptyObject]);

  const removeListItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    onChange(field, updated);
  };

  return (
    <div className="mb-3">
      <h5 className="mt-3 text-success">{title}</h5>
      {items.map((item, idx) => (
        <div key={idx} className="border rounded p-3 mb-2">
          <div className="row">
            {schema.map(({ key, placeholder, col = 4, type = "text" }) => (
              <div className={`col-md-${col} mb-2`} key={key}>
                <input
                  type={type}
                  className="form-control"
                  placeholder={placeholder}
                  value={item[key] ?? ""}
                  onChange={(e) => handleListChange(idx, key, e.target.value)}
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => removeListItem(idx)}
          >
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-light" onClick={addListItem}>
        Add {title.slice(0, -1)}
      </button>
    </div>
  );
};

// ✅ Main ProductOverviewForm
export default function ProductOverviewForm({ data, onChange, onNext, errors, isEdit }) {
  const dataAvailableChecks = [
    ["stability", "Stability"],
    ["toxicity", "Toxicity"],
    ["activity", "Activity"],
    ["clinical", "Clinical"],
  ];

  return (
    <div>
      <h4 className="text-success">Product Overview</h4>

      {/* Basic Information */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="productCode">Product Code *</label>
          <input
            id="productCode"
            type="text"
            className={`form-control ${errors?.productCode ? "is-invalid" : ""}`}
            value={data.productCode ?? ""}
            onChange={(e) => onChange("productCode", e.target.value)}
            required
            disabled={isEdit}
          />
          {errors?.productCode && (
            <div className="invalid-feedback">{errors.productCode}</div>
          )}
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="productName">Product Name *</label>
          <input
            id="productName"
            type="text"
            className={`form-control ${errors?.productName ? "is-invalid" : ""}`}
            value={data.productName ?? ""}
            onChange={(e) => onChange("productName", e.target.value)}
            disabled={isEdit}
          />
          {errors?.productName && (
            <div className="invalid-feedback">{errors.productName}</div>
          )}
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="ingredientSources">Ingredient Source</label>
          <input
            id="ingredientSources"
            type="text"
            className="form-control"
            value={data.ingredientSources ?? ""}
            onChange={(e) => onChange("ingredientSources", e.target.value)}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="productCategory">Product Category</label>
          <input
            id="productCategory"
            type="text"
            className="form-control"
            value={data.productCategory ?? ""}
            onChange={(e) => onChange("productCategory", e.target.value)}
          />
        </div>

        <div className="col-md-4 mb-3">
          <label htmlFor="scope">Scope</label>
          <input
            id="scope"
            type="text"
            className="form-control"
            value={data.scope ?? ""}
            onChange={(e) => onChange("scope", e.target.value)}
          />
        </div>

        <div className="col-md-4 mb-3">
          <label htmlFor="origin">Origin</label>
          <input
            id="origin"
            type="text"
            className="form-control"
            value={data.origin ?? ""}
            onChange={(e) => onChange("origin", e.target.value)}
          />
        </div>

        <div className="col-md-4 mb-3">
          <label htmlFor="form">Form</label>
          <input
            id="form"
            type="text"
            className="form-control"
            value={data.form ?? ""}
            onChange={(e) => onChange("form", e.target.value)}
          />
        </div>
      </div>

          {/* Product Description */}
      <div className="mb-3">
        <label htmlFor="productDescription">Product Description</label>
        <textarea
          id="productDescription"
          className="form-control"
          value={data.productDescription ?? ""}
          onChange={(e) => onChange("productDescription", e.target.value)}
        />
      </div>

      {/* Banner Images */}
      <div className="mb-3">
        <label className="form-label">Banner Images</label>

        {/* Existing + New previews */}
        <div className="d-flex flex-wrap gap-3 mb-2">
          {(data.bannerImages || []).map((img, idx) => {
            const src =
              typeof img === "string"
                ? img                     // signed URL from backend
                : URL.createObjectURL(img); // new file preview

            return (
              <div key={idx} style={{ position: "relative" }}>
                <img
                  src={src}
                  alt="banner"
                  style={{
                    width: 140,
                    height: 140,
                    objectFit: "cover",
                    borderRadius: 8,
                    border: "1px solid #ddd"
                  }}
                />

                {/* remove button */}
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    padding: 0
                  }}
                  onClick={() => {
                    const updated = [...(data.bannerImages || [])];
                    updated.splice(idx, 1);
                    onChange("bannerImages", updated);
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>

        {/* upload multiple */}
        <input
          type="file"
          accept="image/*"
          multiple
          className="form-control"
          onChange={(e) => {
            const files = Array.from(e.target.files);
            const updated = [...(data.bannerImages || []), ...files];
            onChange("bannerImages", updated);
          }}
        />
      </div>


      {/* Actives */}
      <DynamicListSection
        title="Actives"
        field="actives"
        data={data}
        onChange={onChange}
        schema={[
          { key: "assay", placeholder: "Assay" },
          { key: "specification", placeholder: "Specification (Unit)" },
          { key: "assayMethod", placeholder: "Assay Method" },
        ]}
        emptyObject={{ assay: "", specification: "", assayMethod: "" }}
      />

      {/* ✅ Product Compliance as fixed table */}
      <ComplianceTable data={data.productCompliance} onChange={onChange} />

      {/* Ingredient Composition */}
      <DynamicListSection
        title="Ingredient Compositions"
        field="ingredientComposition"
        data={data}
        onChange={onChange}
        schema={[
          { key: "ingredient", placeholder: "Ingredient", col: 6 },
          { key: "percentage", placeholder: "% (e.g., 80%)", col: 3 },
          { key: "type", placeholder: "Type (Dry/Wet)", col: 3 },
        ]}
        emptyObject={{ ingredient: "", percentage: "", type: "" }}
      />

      {/* Process Description */}
      <div className="mb-3">
        <label htmlFor="processDescription">Process Description</label>
        <textarea
          id="processDescription"
          className="form-control"
          value={data.processDescription ?? ""}
          onChange={(e) => onChange("processDescription", e.target.value)}
        />
      </div>

      {/* Processing Aids */}
      <DynamicListSection
        title="Processing Aids"
        field="productProcessingAids"
        data={data}
        onChange={onChange}
        schema={[
          { key: "type", placeholder: "Type", col: 6 },
          { key: "purpose", placeholder: "Purpose", col: 6 },
        ]}
        emptyObject={{ type: "", purpose: "" }}
      />

      {/* Unique Proposition */}
      <div className="mb-3">
        <label htmlFor="uniqueProposition">Unique Proposition</label>
        <textarea
          id="uniqueProposition"
          className="form-control"
          value={data.uniqueProposition ?? ""}
          onChange={(e) => onChange("uniqueProposition", e.target.value)}
        />
      </div>

      {/* Data Available */}
      <h4 className="mt-3 text-success">Data Available</h4>
      {dataAvailableChecks.map(([key, label]) => (
        <div className="form-check" key={key}>
          <input
            id={key}
            type="checkbox"
            className="form-check-input"
            checked={!!data[key]}
            onChange={(e) => onChange(key, e.target.checked)}
          />
          <label className="form-check-label" htmlFor={key}>
            {label}
          </label>
        </div>
      ))}

      {/* Benefits */}
      <div className="mb-3 mt-3">
        <label htmlFor="benefits">Benefits</label>
        <textarea
          id="benefits"
          className="form-control"
          value={data.benefits ?? ""}
          onChange={(e) => onChange("benefits", e.target.value)}
        />
      </div>

      {/* Recommended Use */}
      <div className="mb-3">
        <label htmlFor="recommendedUse">Recommended Use</label>
        <textarea
          id="recommendedUse"
          className="form-control"
          value={data.recommendedUse ?? ""}
          onChange={(e) => onChange("recommendedUse", e.target.value)}
        />
      </div>

      {/* Directions for Use */}
      <div className="mb-3">
        <label htmlFor="directionsForUse">Directions for Use</label>
        <textarea
          id="directionsForUse"
          className="form-control"
          value={data.directionsForUse ?? ""}
          onChange={(e) => onChange("directionsForUse", e.target.value)}
        />
      </div>

      {/* Next Button */}
      <div className="d-flex justify-content-end mt-3">
        <button type="button" className="btn btn-primary" onClick={onNext}>
          Next
        </button>
      </div>
    </div>
  );
}
